import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import PaginationBar from './PaginationBar';
import MultiSelectFilter from './MultiSelectFilter';
import SortDropdown from './SortDropdown';
import api from '../lib/api';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Date formatter: YYYY-MM-DD → DD-MM-YYYY
const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
  return d;
};

const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];

// Sort variants color-wise: group by color, then sizes within each color
const sortColorWise = (variants) => {
  if (!variants || variants.length === 0) return [];
  const colorOrder = [];
  const colorMap = {};
  for (const v of variants) {
    if (!colorMap[v.color]) { colorMap[v.color] = []; colorOrder.push(v.color); }
    colorMap[v.color].push(v);
  }
  const sorted = [];
  for (const color of colorOrder) {
    colorMap[color].sort((a, b) => SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size));
    sorted.push(...colorMap[color]);
  }
  return sorted;
};

// ============ Main Component ============
const PurchaseOrderManagement = ({ user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const bulk = useBulkSelect();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [], fabrics: [], categories: [], design_themes: [] });
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labelsMenuPO, setLabelsMenuPO] = useState(null);

  // Filters
  const [filterVendors, setFilterVendors] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterColors, setFilterColors] = useState([]);
  const [filterSizes, setFilterSizes] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterOccasions, setFilterOccasions] = useState([]);
  const [filterFabrics, setFilterFabrics] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterOrderType, setFilterOrderType] = useState('');
  const [filterStaff, setFilterStaff] = useState([]);
  const [searchPO, setSearchPO] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const searchTimerRef = useRef(null);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onEscape: () => { setLabelsMenuPO(null); },
    onNew: () => navigate('/create-purchase-order'),
  });

  // Purchase Entry dialog state
  const [purchaseEntryPO, setPurchaseEntryPO] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [entryQty, setEntryQty] = useState({});
  const [savingDelivery, setSavingDelivery] = useState(false);

  useEffect(() => { loadAllData(); }, []);

  const loadAllData = async () => {
    try {
      const [ordersRes, vendorsRes, productsRes, enumsRes, brandsRes] = await Promise.allSettled([
        api.get('/purchase-orders'), api.get('/vendors'), api.get('/products'),
        api.get('/enums'), api.get('/brands'),
      ]);
      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data);
        setTotalCount(parseInt(ordersRes.value.headers?.['x-total-count'] || ordersRes.value.data.length));
        setCurrentPage(1);
      }
      if (vendorsRes.status === 'fulfilled') setVendors(vendorsRes.value.data);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
      if (enumsRes.status === 'fulfilled') setEnums(enumsRes.value.data);
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const loadFilteredOrders = async (page = 1, searchTerm = null) => {
    try {
      const params = new URLSearchParams();
      if (filterVendors.length) params.append('vendor_id', filterVendors.join(','));
      if (filterStatuses.length) params.append('status', filterStatuses.join(','));
      if (filterBrands.length) params.append('brand_id', filterBrands.join(','));
      if (filterColors.length) params.append('color', filterColors.join(','));
      if (filterSizes.length) params.append('size', filterSizes.join(','));
      if (filterCategories.length) params.append('category', filterCategories.join(','));
      if (filterOccasions.length) params.append('occasion', filterOccasions.join(','));
      if (filterFabrics.length) params.append('fabric', filterFabrics.join(','));
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      if (filterOrderType) params.append('order_type', filterOrderType);
      const q = searchTerm !== null ? searchTerm : searchPO;
      if (q && q.trim()) params.append('search', q.trim());
      params.append('page', page);
      params.append('limit', PAGE_SIZE);
      const res = await api.get(`/purchase-orders?${params.toString()}`);
      setOrders(res.data);
      setTotalCount(parseInt(res.headers?.['x-total-count'] || res.data.length));
      setCurrentPage(page);
    } catch { toast.error('Failed to filter'); }
  };

  const goToPage = (p) => { if (p >= 1 && p <= totalPages) loadFilteredOrders(p); };

  const handleSearchChange = (value) => {
    setSearchPO(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadFilteredOrders(1, value), 400);
  };

  useEffect(() => { if (!loading) { setCurrentPage(1); loadFilteredOrders(1); } }, [filterVendors, filterStatuses, filterBrands, filterColors, filterSizes, filterCategories, filterOccasions, filterFabrics, filterDateFrom, filterDateTo, filterOrderType]);

  const getVendorName = (id) => {
    const v = vendors.find(v => v.id === id);
    if (!v) return 'Unknown';
    const name = v.name || v.company_name || 'Unknown';
    const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city;
    return loc ? `${name} (${loc})` : name;
  };
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';

  const calcItemTotal = (item) => (item.variants || []).reduce((s, v) => s + (v.qty_ordered || 0) * (v.tentative_cost || 0), 0);

  const deletePO = async (poId) => {
    if (!window.confirm('Delete this Purchase Order? This cannot be undone.')) return;
    try {
      await api.delete(`/purchase-orders/${poId}`);
      toast.success('PO deleted');
      loadAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const clonePO = async (poId) => {
    try {
      const res = await api.post(`/purchase-orders/${poId}/clone`);
      toast.success(`PO cloned as ${res.data.po_number}!`);
      loadAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clone PO'); }
  };

  const [shareDialogPO, setShareDialogPO] = useState(null);
  const [shareFormat, setShareFormat] = useState('pdf');
  const [shareAction, setShareAction] = useState('download'); // download, whatsapp, email

  const openLabelsInBrowser = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/barcode-labels?mode=view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Labels opened in new tab');
    } catch { toast.error('Failed to generate labels'); }
    setLabelsMenuPO(null);
  };

  const downloadBarcodeLabels = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/barcode-labels`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}_labels.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF labels downloaded');
    } catch { toast.error('Failed to generate labels'); }
    setLabelsMenuPO(null);
  };

  const downloadBarcodeLabelsExcel = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/barcode-labels-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}_labels.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel labels downloaded');
    } catch { toast.error('Failed to generate labels'); }
    setLabelsMenuPO(null);
  };

  const executeShare = async (po, format, action) => {
    if (action === 'download') {
      try {
        const endpoint = format === 'pdf' ? `/purchase-orders/${po.id}/pdf` : `/purchase-orders/${po.id}/excel`;
        const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const ext = format === 'pdf' ? '.pdf' : '.xlsx';
        const res = await api.get(endpoint, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
        const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}${ext}`; a.click();
        window.URL.revokeObjectURL(url); toast.success(`${format.toUpperCase()} downloaded!`);
      } catch { toast.error(`${format.toUpperCase()} generation failed`); }
    } else if (action === 'whatsapp') {
      try {
        // Download PDF and share via Web Share API (supports file sharing on mobile)
        const res = await api.get(`/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
        const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
        const pdfFile = new File([pdfBlob], `${po.po_number}.pdf`, { type: 'application/pdf' });

        if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
          // Mobile: use native share sheet which includes WhatsApp
          await navigator.share({
            title: `PO: ${po.po_number}`,
            text: `Purchase Order ${po.po_number}`,
            files: [pdfFile],
          });
        } else {
          // Desktop fallback: download PDF, then open WhatsApp with text
          const url = window.URL.createObjectURL(pdfBlob);
          const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}.pdf`; a.click();
          window.URL.revokeObjectURL(url);
          const vendor = vendors.find(v => v.id === po.vendor_id);
          const text = encodeURIComponent(`*PO: ${po.po_number}*\nVendor: ${vendor?.name || 'Unknown'}\nDate: ${fmtDate(po.po_date)}\nTotal: ₹${(po.total_amount || 0).toLocaleString('en-IN')}\n\nPDF attached separately.\n\nFrom: Jini Nimi Fashion LLP`);
          window.open(`https://wa.me/?text=${text}`, '_blank');
          toast.success('PDF downloaded — attach it in WhatsApp');
        }
      } catch { toast.error('Failed to generate PDF for sharing'); }
    } else if (action === 'email') {
      const vendor = vendors.find(v => v.id === po.vendor_id);
      const subject = encodeURIComponent(`Purchase Order ${po.po_number} - Jini Nimi Fashion LLP`);
      const body = encodeURIComponent(`Dear ${vendor?.name || 'Vendor'},\n\nPO: ${po.po_number}\nDate: ${fmtDate(po.po_date)}\nTotal: ₹${(po.total_amount || 0).toLocaleString('en-IN')}\n\nRegards,\nJini Nimi Fashion LLP`);
      window.open(`mailto:${vendor?.email || ''}?subject=${subject}&body=${body}`, '_self');
    }
    setShareDialogPO(null);
  };

  const renderShareDialog = () => {
    if (!shareDialogPO) return null;
    return (
      <Dialog open={!!shareDialogPO} onOpenChange={() => setShareDialogPO(null)}>
        <DialogContent className="max-w-sm z-50">
          <DialogHeader>
            <DialogTitle>Share {shareDialogPO.po_number}</DialogTitle>
            <DialogDescription>Choose format and sharing method</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Format</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShareFormat('pdf')} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'pdf' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300'}`} data-testid="share-format-pdf">
                  <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  <span className="text-sm font-medium">PDF</span>
                </button>
                <button type="button" onClick={() => setShareFormat('excel')} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'excel' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-300'}`} data-testid="share-format-excel">
                  <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <span className="text-sm font-medium">Excel</span>
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Share via</Label>
              <div className="flex flex-col gap-2">
                <Button onClick={() => executeShare(shareDialogPO, shareFormat, 'download')} className="justify-start" data-testid="share-action-download">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Download {shareFormat.toUpperCase()}
                </Button>
                <Button variant="outline" className="justify-start text-green-700 border-green-300 hover:bg-green-50" onClick={() => executeShare(shareDialogPO, shareFormat, 'whatsapp')} data-testid="share-action-whatsapp">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </Button>
                <Button variant="outline" className="justify-start text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => executeShare(shareDialogPO, shareFormat, 'email')} data-testid="share-action-email">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  Email
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const openPurchaseEntry = (po) => {
    setPurchaseEntryPO(po);
    setPurchaseDate(new Date().toISOString().slice(0, 10));
    setInvoiceNumber('');
    const qty = {};
    for (const item of (po.items || [])) {
      qty[item.id] = {};
      for (const v of (item.variants || [])) {
        qty[item.id][`${v.color}_${v.size}`] = 0;
      }
    }
    setEntryQty(qty);
  };

  const updateEntryQty = (itemId, color, size, val) => {
    setEntryQty(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [`${color}_${size}`]: parseInt(val) || 0 },
    }));
  };

  const savePurchaseEntry = async () => {
    if (!purchaseDate) { toast.error('Enter purchase date'); return; }
    if (!invoiceNumber.trim()) { toast.error('Enter invoice number'); return; }
    setSavingDelivery(true);
    try {
      for (const item of (purchaseEntryPO.items || [])) {
        const itemQty = entryQty[item.id] || {};
        const hasQty = Object.values(itemQty).some(q => q > 0);
        if (!hasQty) continue;

        // Add new entry quantities to existing received
        const deliveries = (item.variants || []).map(v => {
          const newQty = itemQty[`${v.color}_${v.size}`] || 0;
          return { size: v.size, color: v.color, qty_received: (v.qty_received || 0) + newQty };
        });
        await api.put(`/purchase-orders/${purchaseEntryPO.id}/items/${item.id}/delivery`, {
          variant_deliveries: deliveries,
          purchase_date: purchaseDate,
          invoice_number: invoiceNumber,
        });
      }
      toast.success(`Purchase entry saved! Invoice: ${invoiceNumber}`);
      setPurchaseEntryPO(null);
      loadAllData();
    } catch { toast.error('Failed to save purchase entry'); }
    finally { setSavingDelivery(false); }
  };

  const STATUS_COLORS = {
    draft: 'bg-gray-100 text-gray-700', confirmed: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    partial_received: 'bg-amber-100 text-amber-700', completed: 'bg-green-100 text-green-700', closed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    in_production: 'bg-purple-100 text-purple-700', dispatched: 'bg-blue-100 text-blue-700',
    received: 'bg-green-100 text-green-700',
  };

  const APPROVAL_COLORS = {
    pending_approval: 'bg-orange-100 text-orange-800 border border-orange-300',
    approved: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
    rejected: 'bg-red-100 text-red-700 border border-red-300',
  };

  const approvalLabel = (s) => {
    const labels = { pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
    return labels[s] || s;
  };

  const statusLabel = (s) => {
    const labels = { confirmed: 'Pending', partial_received: 'Partial Received', completed: 'Completed', closed: 'Closed', cancelled: 'Cancelled', draft: 'Draft', pending: 'Pending', in_production: 'In Production', dispatched: 'Dispatched', received: 'Received' };
    return labels[s] || s?.replace('_', ' ') || '';
  };

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff' || user?.permissions?.can_create_po;

  const CANCEL_REASONS = ['Late Delivery', 'Quality Issues', 'Price Change', 'Order Cancelled', 'Out of Stock', 'Other'];
  const [cancellingItem, setCancellingItem] = useState(null);
  const [cancelReason, setCancelReason] = useState('Late Delivery');
  const [cancelNotes, setCancelNotes] = useState('');

  const cancelPOItem = async () => {
    if (!cancellingItem) return;
    try {
      const res = await api.put(`/purchase-orders/${cancellingItem.poId}/items/${cancellingItem.itemId}/cancel`, { reason: cancelReason, notes: cancelNotes });
      toast.success('Item cancelled');
      setOrders(prev => prev.map(o => o.id === cancellingItem.poId ? res.data : o));
      setCancellingItem(null); setCancelReason('Late Delivery'); setCancelNotes('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel item'); }
  };

  // Client-side PO number search filter
  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'A to Z' },
    { value: 'name_desc', label: 'Z to A' },
    { value: 'qty_desc', label: 'Qty: High to Low' },
    { value: 'qty_asc', label: 'Qty: Low to High' },
    { value: 'amount_desc', label: 'Amount: High to Low' },
    { value: 'amount_asc', label: 'Amount: Low to High' },
  ];

  const filteredOrders = useMemo(() => {
    const vendorMap = Object.fromEntries(vendors.map(v => [v.id, v]));
    let list = searchPO
      ? orders.filter(po => {
          const q = searchPO.toLowerCase();
          const matchesName = getVendorName(po.vendor_id)?.toLowerCase().includes(q);
          const matchesNumber = /\d/.test(searchPO) && po.po_number?.toLowerCase().includes(q);
          return matchesName || matchesNumber;
        })
      : [...orders];
    if (filterStaff.length) list = list.filter(po => filterStaff.includes(po.created_by_name));
    if (filterStates.length) list = list.filter(po => filterStates.includes(vendorMap[po.vendor_id]?.state));
    if (filterCities.length) list = list.filter(po => filterCities.includes(vendorMap[po.vendor_id]?.city));
    list.sort((a, b) => {
      const vA = getVendorName(a.vendor_id), vB = getVendorName(b.vendor_id);
      switch (sortBy) {
        case 'date_asc': return new Date(a.po_date || a.created_at) - new Date(b.po_date || b.created_at);
        case 'name_asc': return vA.localeCompare(vB);
        case 'name_desc': return vB.localeCompare(vA);
        case 'qty_desc': return (b.total_qty || 0) - (a.total_qty || 0);
        case 'qty_asc': return (a.total_qty || 0) - (b.total_qty || 0);
        case 'amount_desc': return (b.total_amount || 0) - (a.total_amount || 0);
        case 'amount_asc': return (a.total_amount || 0) - (b.total_amount || 0);
        default: return new Date(b.po_date || b.created_at) - new Date(a.po_date || a.created_at);
      }
    });
    return list;
  }, [orders, searchPO, sortBy, filterStaff, filterStates, filterCities, vendors]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  const statusOptions = [
    { value: 'confirmed', label: 'Pending' }, { value: 'partial_received', label: 'Partial Received' },
    { value: 'completed', label: 'Completed' }, { value: 'closed', label: 'Closed' }, { value: 'cancelled', label: 'Cancelled' },
  ];
  const vendorOptions = vendors.map(v => ({ value: v.id, label: v.name }));
  const stateOptions = [...new Set(vendors.map(v => v.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const cityOptions = [...new Set(vendors.filter(v => !filterStates.length || filterStates.includes(v.state)).map(v => v.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const brandOptions = brands.map(b => ({ value: b.id, label: b.name }));
  const colorOptions = (enums.colors || []).map(c => ({ value: c, label: c }));
  const sizeOptions = (enums.sizes || []).map(s => ({ value: s, label: s }));
  const staffOptions = [...new Set(orders.map(o => o.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));

  // ============ Purchase Entry Dialog ============
  const renderPurchaseEntryDialog = () => {
    if (!purchaseEntryPO) return null;
    const po = purchaseEntryPO;
    return (
      <Dialog open={!!purchaseEntryPO} onOpenChange={() => setPurchaseEntryPO(null)}>
        <DialogContent className="max-w-4xl z-[60] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Entry - {po.po_number}</DialogTitle>
            <DialogDescription>Record received goods for {getVendorName(po.vendor_id)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Date *</Label>
                <Input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} data-testid="purchase-entry-date" />
              </div>
              <div className="space-y-2">
                <Label>Invoice Number *</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2026-001" data-testid="purchase-entry-invoice" />
              </div>
            </div>

            {(po.items || []).map((item, idx) => {
              const sortedVariants = sortColorWise(item.variants || []);
              return (
              <Card key={idx} className="border-l-4 border-l-blue-400" data-testid={`purchase-entry-item-${idx}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    {item.images?.[0] && <img src={`${API_BASE}${item.images[0]}`} alt={item.design_code} className="w-10 h-10 rounded object-cover" />}
                    <span className="font-semibold text-sm">Design #{item.design_code}</span>
                    {item.brand_id && <Badge className="bg-indigo-100 text-indigo-700 text-xs">{getBrandName(item.brand_id)}</Badge>}
                  </div>
                  <table className="w-full text-xs border rounded">
                    <thead className="bg-gray-50"><tr>
                      <th className="p-1.5 text-left">Color</th><th className="p-1.5 text-left">Size</th>
                      <th className="p-1.5 text-right">Ordered</th><th className="p-1.5 text-right">Already Recd</th>
                      <th className="p-1.5 text-right">Still Pending</th>
                      <th className="p-1.5 text-center bg-blue-50 border-l-2 border-blue-200">Qty Now</th>
                    </tr></thead>
                    <tbody>
                      {sortedVariants.map((v, vi) => {
                        const alreadyRec = v.qty_received || 0;
                        const stillPending = Math.max(0, (v.qty_ordered || 0) - alreadyRec);
                        const entryVal = entryQty[item.id]?.[`${v.color}_${v.size}`] || 0;
                        return (
                        <tr key={vi} className="border-t">
                          <td className="p-1.5 font-medium">{v.color}</td>
                          <td className="p-1.5">{v.size}</td>
                          <td className="p-1.5 text-right">{v.qty_ordered}</td>
                          <td className="p-1.5 text-right">{alreadyRec}</td>
                          <td className="p-1.5 text-right"><span className={stillPending > 0 ? 'text-red-600' : 'text-green-600'}>{stillPending}</span></td>
                          <td className="p-1.5 text-center bg-blue-50/50 border-l-2 border-blue-200">
                            {stillPending > 0 ? (
                              <input type="number" className="w-14 text-center text-xs border rounded px-1 h-6 font-medium"
                                value={entryVal || ''} min="0" max={stillPending}
                                onChange={e => updateEntryQty(item.id, v.color, v.size, e.target.value)}
                                data-testid={`entry-qty-${idx}-${vi}`} />
                            ) : (
                              <span className="text-green-600 text-[10px]">Done</span>
                            )}
                          </td>
                        </tr>);
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>);
            })}

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setPurchaseEntryPO(null)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={savePurchaseEntry} disabled={savingDelivery} data-testid="save-purchase-entry">
                {savingDelivery ? 'Saving...' : 'Save Purchase Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // ============ RENDER ============
  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="po-management-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Purchase Orders</h1>
          <p className="text-gray-600 mt-1">Create and manage purchase orders with vendors</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="po-filters">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[180px]">
              <Label className="text-xs mb-1 block">Search PO</Label>
              <Input value={searchPO} onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search by PO number or vendor..." className="h-9 text-sm"
                data-testid="search-po-input" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Sort By</Label>
              <SortDropdown value={sortBy} onChange={setSortBy} options={sortOptions} testId="po-sort" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Vendor</Label>
              <MultiSelectFilter label="All Vendors" options={vendorOptions} selected={filterVendors} onChange={setFilterVendors} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">State</Label>
              <MultiSelectFilter label="All States" options={stateOptions} selected={filterStates} onChange={setFilterStates} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">City</Label>
              <MultiSelectFilter label="All Cities" options={cityOptions} selected={filterCities} onChange={setFilterCities} />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Status</Label>
              <MultiSelectFilter label="All Status" options={statusOptions} selected={filterStatuses} onChange={setFilterStatuses} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Brand</Label>
              <MultiSelectFilter label="All Brands" options={brandOptions} selected={filterBrands} onChange={setFilterBrands} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Color</Label>
              <MultiSelectFilter label="All Colors" options={colorOptions} selected={filterColors} onChange={setFilterColors} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Size</Label>
              <MultiSelectFilter label="All Sizes" options={sizeOptions} selected={filterSizes} onChange={setFilterSizes} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Category</Label>
              <MultiSelectFilter label="All Categories" options={(enums.categories || []).map(c => ({ value: c, label: c }))} selected={filterCategories} onChange={setFilterCategories} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Occasion</Label>
              <MultiSelectFilter label="All Occasions" options={(enums.design_themes || []).map(o => ({ value: o, label: o }))} selected={filterOccasions} onChange={setFilterOccasions} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Fabric</Label>
              <MultiSelectFilter label="All Fabrics" options={(enums.fabrics || []).map(f => ({ value: f, label: f }))} selected={filterFabrics} onChange={setFilterFabrics} />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">Date From</Label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9 text-xs" data-testid="po-filter-date-from" />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">Date To</Label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9 text-xs" data-testid="po-filter-date-to" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Order Type</Label>
              <select value={filterOrderType} onChange={e => setFilterOrderType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs" data-testid="po-filter-order-type">
                <option value="">All Orders</option>
                <option value="new">New Orders</option>
                <option value="repeat">Repeat Orders</option>
              </select>
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Staff</Label>
              <MultiSelectFilter label="All Staff" options={staffOptions} selected={filterStaff} onChange={setFilterStaff} testId="po-filter-staff" />
            </div>
            {(filterVendors.length > 0 || filterStatuses.length > 0 || filterStates.length > 0 || filterCities.length > 0 || filterBrands.length > 0 || filterColors.length > 0 || filterSizes.length > 0 || filterCategories.length > 0 || filterOccasions.length > 0 || filterFabrics.length > 0 || filterDateFrom || filterDateTo || filterOrderType || filterStaff.length > 0 || searchPO) && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setFilterVendors([]); setFilterStatuses([]); setFilterStates([]); setFilterCities([]); setFilterBrands([]); setFilterColors([]); setFilterSizes([]); setFilterCategories([]); setFilterOccasions([]); setFilterFabrics([]); setFilterDateFrom(''); setFilterDateTo(''); setFilterOrderType(''); setFilterStaff([]); setSearchPO(''); }} data-testid="clear-filters">Clear All</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* PO List — Excel-like table */}
      <Card data-testid="po-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Purchase Orders ({totalCount})</CardTitle>
            {canManage && (
              <Button size="sm" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-sm" onClick={() => navigate('/create-purchase-order')} data-testid="create-po-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Create PO
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 px-4">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Purchase Orders</h3>
              <p className="text-gray-600">Create your first PO or adjust filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="po-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll((filteredOrders || orders).map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="px-4 py-3 text-left font-medium">PO #</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Vendor</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Ordered</th>
                    <th className="px-4 py-3 text-right font-medium">Received</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-center font-medium">Progress</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                    <th className="px-4 py-3 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrders.map(po => {
                    const ordered = (po.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_ordered || 0), 0), 0);
                    const received = (po.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_received || 0), 0), 0);
                    const pct = ordered > 0 ? Math.round(received / ordered * 100) : 0;
                    return (
                      <tr
                        key={po.id}
                        className={`hover:bg-emerald-50/50 cursor-pointer transition-colors group ${bulk.isSelected(po.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => navigate(`/purchase-order/${po.id}`)}
                        data-testid={`po-row-${po.id}`}
                      >
                        <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(po.id)} onChange={() => bulk.toggleSelect(po.id)} /></td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-emerald-700 font-mono group-hover:underline">{po.po_number}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {fmtDate(po.po_date)}
                          {po.created_at && <span className="block text-[10px] text-gray-400">{new Date(po.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{getVendorName(po.vendor_id)}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{po.items?.length || 0}</td>
                        <td className="px-4 py-3 text-right font-medium">{ordered}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={received >= ordered && ordered > 0 ? 'text-green-600 font-semibold' : received > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}>{received}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(po.total_amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-gray-300'}`} style={{ width: `${Math.min(100, pct)}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${STATUS_COLORS[po.status] || 'bg-gray-100'} text-[11px]`}>{statusLabel(po.status)}</Badge>
                          {po.approval_status && po.approval_status !== 'approved' && (
                            <Badge className={`ml-1 ${APPROVAL_COLORS[po.approval_status]} text-[10px]`}>{approvalLabel(po.approval_status)}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{po.created_by_name || '-'}</td>
                        <td className="px-2 py-3 text-center" onClick={e => e.stopPropagation()}>
                          {po.approval_status === 'approved' ? (
                            received < ordered ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => navigate('/create-purchase-entry', { state: { fromPO: po } })}
                                data-testid={`po-create-pe-${po.id}`}
                                title="Create Purchase Entry against this PO"
                              >
                                + Create PE
                              </Button>
                            ) : (
                              <span className="text-[10px] text-emerald-600 font-medium" data-testid={`po-pe-complete-${po.id}`}>Fully received</span>
                            )
                          ) : (
                            <span className="text-[10px] text-amber-600" data-testid={`po-pe-blocked-${po.id}`} title="PO awaits admin approval">Pending approval</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginationBar currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={goToPage} testIdPrefix="po-page" />

      {renderPurchaseEntryDialog()}
      {renderShareDialog()}

      {/* Cancel Item Dialog */}
      <Dialog open={!!cancellingItem} onOpenChange={v => { if (!v) setCancellingItem(null); }}>
        <DialogContent className="max-w-md z-50">
          <DialogHeader>
            <DialogTitle>Cancel PO Item</DialogTitle>
            <DialogDescription>Provide a reason for cancelling this item</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Reason *</Label>
              <select value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="cancel-item-reason">
                {CANCEL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={cancelNotes} onChange={e => setCancelNotes(e.target.value)} placeholder="Additional notes..." data-testid="cancel-item-notes" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCancellingItem(null)}>Go Back</Button>
              <Button onClick={cancelPOItem} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-item">Confirm Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="PO" label="purchase orders" onComplete={() => { bulk.clearSelection(); loadAllData(); }} onClear={bulk.clearSelection}
        extraActions={[{
          label: 'Bulk Approve',
          testId: 'bulk-po-approve-btn',
          className: 'text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30',
          onClick: async () => {
            if (!window.confirm(`Approve ${bulk.selectedCount} PO(s)?`)) return;
            try { await api.post('/purchase-orders/bulk-status', { ids: bulk.selectedIds, action: 'approve' }); toast.success(`${bulk.selectedCount} PO(s) approved`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }, {
          label: 'Bulk Close',
          testId: 'bulk-po-close-btn',
          className: 'text-amber-300 hover:text-amber-200 hover:bg-amber-900/30',
          onClick: async () => {
            if (!window.confirm(`Close ${bulk.selectedCount} PO(s)?`)) return;
            try { await api.post('/purchase-orders/bulk-status', { ids: bulk.selectedIds, action: 'close' }); toast.success(`${bulk.selectedCount} PO(s) closed`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }, {
          label: 'Bulk Cancel',
          testId: 'bulk-po-cancel-btn',
          className: 'text-red-300 hover:text-red-200 hover:bg-red-900/30',
          onClick: async () => {
            if (!window.confirm(`Cancel ${bulk.selectedCount} PO(s)?`)) return;
            try { await api.post('/purchase-orders/bulk-status', { ids: bulk.selectedIds, action: 'cancel' }); toast.success(`${bulk.selectedCount} PO(s) cancelled`); bulk.clearSelection(); loadAllData(); } catch { toast.error('Failed'); }
          },
        }]} />
    </div>
  );
};

export default PurchaseOrderManagement;
